import React, { createContext, useContext, useMemo, PropsWithChildren } from 'react';
import {
  calculateXPositionForTime,
  calculateTimeForXPosition,
  SelectUnits,
} from '../utility/calendar';
import type { TimelineContext as TimelineContextValue } from '../types/main';
import type { Dayjs } from 'dayjs';

const defaultContextState = {
  getTimelineState: () => {
    console.warn('"getTimelineState" default func is being used');
    return {} as TimelineContextValue;
  },
  getLeftOffsetFromDate: () => {
    console.warn('"getLeftOffsetFromDate" default func is being used');
    return 0;
  },
  getDateFromLeftOffsetPosition: () => {
    console.warn('"getDateFromLeftOffsetPosition" default func is being used');
    return 0;
  },
  showPeriod: () => {
    console.warn('"showPeriod" default func is being used');
  },
};

export const TimelineContext = createContext<TimelineContextType>(
  defaultContextState as TimelineContextType
);

export interface TimelineContextType {
  getTimelineState: () => TimelineContextValue;
  getLeftOffsetFromDate: (date: number) => number;
  getDateFromLeftOffsetPosition: (leftOffset: number) => number;
  showPeriod: (from: Dayjs, to: Dayjs) => void;
}

interface TimelineStartProps {
  visibleTimeStart: number;
  visibleTimeEnd: number;
  canvasTimeStart: number;
  canvasTimeEnd: number;
  canvasWidth: number;
  showPeriod: (from: Dayjs, to: Dayjs) => void;
  timelineUnit: SelectUnits;
  timelineWidth: number;
}

export function TimelineStateProvider(props: PropsWithChildren<TimelineStartProps>) {
  const {
    visibleTimeStart,
    visibleTimeEnd,
    canvasTimeStart,
    canvasTimeEnd,
    canvasWidth,
    showPeriod,
    timelineUnit,
    timelineWidth,
  } = props;

  const timelineContext = useMemo<TimelineContextType>(
    () => ({
      getTimelineState: () =>
        ({
          visibleTimeStart,
          visibleTimeEnd,
          canvasTimeStart,
          canvasTimeEnd,
          canvasWidth,
          timelineUnit,
          timelineWidth,
        }) as TimelineContextValue,
      getLeftOffsetFromDate: (date: number) =>
        calculateXPositionForTime(canvasTimeStart, canvasTimeEnd, canvasWidth, date),
      getDateFromLeftOffsetPosition: (leftOffset: number) =>
        calculateTimeForXPosition(canvasTimeStart, canvasTimeEnd, canvasWidth, leftOffset),
      showPeriod,
    }),
    [
      visibleTimeStart,
      visibleTimeEnd,
      canvasTimeStart,
      canvasTimeEnd,
      canvasWidth,
      timelineUnit,
      timelineWidth,
      showPeriod,
    ]
  );

  return (
    <TimelineContext.Provider value={timelineContext}>{props.children}</TimelineContext.Provider>
  );
}

TimelineStateProvider.displayName = 'TimelineStateProvider';

export const TimelineStateConsumer = TimelineContext.Consumer;
export const useTimelineState = () => useContext(TimelineContext);
